import { useParams } from "react-router-dom";
import { useAppStore } from "../../store/useAppStore";
import { ContextHeader } from "../../components/layout/ContextHeader";
import { StateTrack } from "../../components/ui/StateTrack";
import { Button } from "../../components/ui/Button";
import { stepStatus, moveOutMachine } from "../../domain/stateMachines";
import type { MoveOutState } from "../../domain/stateMachines";

const nextStage: Record<MoveOutState, MoveOutState | null> = {
  notice: "pre-walk",
  "pre-walk": "move-out-inspection",
  "move-out-inspection": "turnover-actions",
  "turnover-actions": "ready",
  ready: null
};

const stageAction: Record<MoveOutState, string> = {
  notice: "Confirm pre-walk scheduled",
  "pre-walk": "Complete move-out inspection",
  "move-out-inspection": "Begin turnover work",
  "turnover-actions": "Mark unit ready",
  ready: ""
};

export function MoveOutFlow() {
  const { planId } = useParams();
  const plan = useAppStore((s) => s.moveOutPlans.find((p) => p.id === planId));
  const unit = useAppStore((s) => s.units.find((u) => u.id === plan?.unitId));
  const site = useAppStore((s) => s.sites.find((si) => si.id === unit?.siteId));
  const workOrders = useAppStore((s) => s.workOrders.filter((w) => w.unitId === plan?.unitId));
  const advanceMoveOut = useAppStore((s) => s.advanceMoveOut);

  if (!plan || !unit) return <p className="p-6 text-[13.5px] text-muted-foreground">Turnover plan not found.</p>;

  const steps = stepStatus(moveOutMachine, plan.stage);
  const upcoming = nextStage[plan.stage];

  return (
    <div className="flex flex-1 flex-col">
      <ContextHeader onBack crumb={`${site?.name.toUpperCase()} › ${unit.buildingLabel.toUpperCase()}`} title={`Turnover — ${unit.unitNumber}`} subtitle={`Notice ${plan.noticeDate} → move-out ${plan.moveOutDate}`} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <div className="rounded-md border border-border bg-card p-3.5">
          <StateTrack steps={steps} />
        </div>

        {plan.stage === "turnover-actions" && workOrders.length > 0 && (
          <div className="rounded-md border border-border bg-card p-3.5">
            <p className="mb-2 font-action text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Turnover work</p>
            <ul className="flex flex-col gap-2">
              {workOrders.map((wo) => (
                <li key={wo.id} className="flex items-center justify-between rounded-sm border border-border px-2.5 py-2">
                  <span className="text-[13px] text-foreground">{wo.title}</span>
                  <span className="font-mono text-[10.5px] uppercase text-muted-foreground">{wo.status.replace(/-/g, " ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-md border border-border bg-card p-3.5">
          <p className="mb-2 font-action text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Deposit</p>
          <div className="flex justify-between text-[13px]">
            <span className="text-muted-foreground">On file</span>
            <span className="font-mono font-semibold">${plan.depositOnFile.toLocaleString()}</span>
          </div>
        </div>

        {upcoming && (
          <Button className="mt-auto" onClick={() => advanceMoveOut(plan.id, upcoming)}>
            {stageAction[plan.stage]}
          </Button>
        )}
        {plan.stage === "ready" && <p className="mt-auto rounded-md border border-success/40 bg-success/10 p-3 text-center font-action text-[14px] font-bold text-success">Unit ready for next tenant</p>}
      </div>
    </div>
  );
}
